import React from 'react';
import { Link } from '@inertiajs/react';
import StaffLayout from '@/layouts/StaffLayout';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import usePermissions from '@/hooks/usePermissions';
import { AlertCircle, ArrowLeft } from 'lucide-react';

interface ImportJob {
    id: number;
    status: string;
    created_at: string | null;
    academy: { id: number; name: string } | null;
    created: number;
    enrolled: number;
    failed: number;
    total: number;
}

interface HistoryProps {
    jobs: ImportJob[];
}

const STATUS_LABELS: Record<string, string> = {
    pending: 'Pendiente',
    processing: 'Procesando',
    completed: 'Completada',
    failed: 'Fallida',
};

const STATUS_CLASSES: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-700',
    processing: 'bg-blue-100 text-blue-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
};

const formatDate = (value: string | null) => {
    if (!value) return '—';
    return new Date(value).toLocaleString();
};

const HistoryContent: React.FC<HistoryProps> = ({ jobs }) => {
    const { route } = useRoutes();

    return (
        <StaffLayout>
            <div className="mx-auto max-w-5xl">
                <div className="mb-8 flex items-center justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                            Historial de importaciones
                        </h1>
                        <p className="mt-2 text-gray-600 dark:text-neutral-300">
                            Revisa las importaciones que has realizado y su informe.
                        </p>
                    </div>
                    <Link href={route('staff-user-import').toString()}>
                        <span className="flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-neutral-300">
                            <ArrowLeft size={16} className="mr-1" />
                            Nueva importación
                        </span>
                    </Link>
                </div>

                <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-neutral-800">
                    {jobs.length === 0 ? (
                        <div className="p-8 text-center text-sm text-gray-500 dark:text-neutral-400">
                            Aún no has realizado ninguna importación.
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-neutral-700">
                            <thead>
                                <tr>
                                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-neutral-300">
                                        Fecha
                                    </th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-neutral-300">
                                        Estado
                                    </th>
                                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-neutral-300">
                                        Resumen
                                    </th>
                                    <th className="px-4 py-3" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-neutral-700">
                                {jobs.map((job) => (
                                    <tr key={job.id}>
                                        <td className="px-4 py-3 text-gray-700 dark:text-neutral-300">
                                            {formatDate(job.created_at)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                                                    STATUS_CLASSES[job.status] ||
                                                    'bg-gray-100 text-gray-700'
                                                }`}
                                            >
                                                {STATUS_LABELS[job.status] || job.status}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-gray-700 dark:text-neutral-300">
                                            {job.created} creados · {job.enrolled}{' '}
                                            matriculados · {job.failed} con errores
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <Link
                                                href={route('staff-user-import-detail', {
                                                    job_id: job.id,
                                                }).toString()}
                                                className="text-sm font-medium text-theme-primary hover:underline"
                                            >
                                                Ver informe
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </StaffLayout>
    );
};

const History: React.FC<HistoryProps> = (props) => {
    const { canImportUsers } = usePermissions();

    if (!canImportUsers) {
        return (
            <ThemeWrapper>
                <StaffLayout>
                    <div className="mx-auto max-w-3xl py-12 text-center">
                        <AlertCircle
                            size={40}
                            className="mx-auto mb-4 text-red-500"
                        />
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                            No tienes permiso para importar usuarios
                        </h1>
                    </div>
                </StaffLayout>
            </ThemeWrapper>
        );
    }

    return (
        <ThemeWrapper>
            <HistoryContent {...props} />
        </ThemeWrapper>
    );
};

export default History;
